/*
 * Cart drawer. v18 lane E.
 * Public interface used by product-form.js, quick-size-picker.js, shoppable-buy-all.js and
 * bundle-builder.js (keep names and signatures):
 *   setActiveElement(element), renderContents(parsedState, isClosedCart = true), getSectionsToRender()
 * Changes: focus returns to the element that opened the drawer (header icon, add button, sticky
 * button, with visible fallbacks); the drawer is inert while closed; focus rings are only hidden
 * for pointer-initiated opens; cart updates are announced politely without moving focus.
 * Images inside the drawer keep their sources in data-yf-drawer-src / -srcset / -sizes (PERF-02),
 * so a closed drawer requests no images. They are swapped in when the drawer is about to open,
 * on intent (hover, focus or touch on the header cart icon) and after any re-render while open.
 */
(function trackInputModality() {
  if (window.__yfInputModality) return;
  window.__yfInputModality = "pointer";
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.metaKey || e.altKey || e.ctrlKey) return;
      window.__yfInputModality = "keyboard";
      document.body && document.body.classList.remove("no-focus-visible");
    },
    true,
  );
  ["pointerdown", "mousedown", "touchstart"].forEach((type) =>
    document.addEventListener(
      type,
      () => {
        window.__yfInputModality = "pointer";
      },
      { capture: true, passive: true },
    ),
  );
})();

function yfIsFocusable(el) {
  if (!(el instanceof HTMLElement) || !el.isConnected) return false;
  if (el === document.body || el === document.documentElement) return false;
  if (el.hasAttribute("disabled") || el.closest("[inert]")) return false;
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === "hidden" || style.visibility === "collapse" || style.display === "none") return false;
  return true;
}

function yfHydrateDrawerImages(root) {
  if (!root || typeof root.querySelectorAll !== "function") return;
  root.querySelectorAll("img[data-yf-drawer-src]").forEach((img) => {
    const { yfDrawerSrc, yfDrawerSrcset, yfDrawerSizes } = img.dataset;
    // Source deferral already prevents hidden requests. Do not also defer the
    // now-visible image behind the browser's lazy-load intersection scheduling.
    img.setAttribute("loading", "eager");
    img.setAttribute("fetchpriority", "high");
    if (yfDrawerSizes) img.setAttribute("sizes", yfDrawerSizes);
    if (yfDrawerSrcset) img.setAttribute("srcset", yfDrawerSrcset);
    if (yfDrawerSrc) img.setAttribute("src", yfDrawerSrc);
    img.removeAttribute("data-yf-drawer-src");
    img.removeAttribute("data-yf-drawer-srcset");
    img.removeAttribute("data-yf-drawer-sizes");
  });
}

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close--button");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Element that opened the drawer. restoreFocusToOpener() reads it.
    this.openerEl = null;
    this.activeElement = null;
    this.cartRefreshId = 0;
    this.historyRestorePromise = null;
    this.historyRestoreFailed = false;
    this.restoreOpenRequested = false;
    this.onPageHide = this.onPageHide.bind(this);
    this.needsFreshCart = true;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.watchDrawerImages();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (!this.isOpen) return;
      setTabindex(this.toggleEelements(), "0");
      // Keep focus where the shopper is. Only recover it if a re-render removed it.
      if (!this.contains(document.activeElement)) this.closeButton()?.focus();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    this.imageObserver?.disconnect();
    window.removeEventListener("pagehide", this.onPageHide);
  }

  onPageHide() {
    // Cache a closed drawer, so returning from checkout cannot flash an old total.
    this.restoreOpenRequested = false;
    this.close(false);
    this.needsFreshCart = true;
    this.setAttribute("data-yf-cart-pending", "");
  }

  restoreAfterHistory() {
    this.close(false);
    this.restoreOpenRequested = false;
    this.historyRestoreFailed = false;
    this.setAttribute("aria-busy", "true");
    this.setAttribute("data-yf-cart-pending", "");
    const restore = this.refreshCartDrawer();
    this.historyRestorePromise = restore;
    restore.then((fresh) => {
      if (this.historyRestorePromise !== restore) return;
      this.historyRestorePromise = null;
      this.historyRestoreFailed = !fresh;
      this.needsFreshCart = !fresh;
      if (fresh) this.removeAttribute("data-yf-cart-pending");
      this.removeAttribute("aria-busy");
      const shouldOpen = this.restoreOpenRequested;
      this.restoreOpenRequested = false;
      if (shouldOpen && !this.isOpen) this.toggleDrawerClasses();
      if (shouldOpen && !fresh) window.location.assign(window.routes.cart_url);
    });
  }

  hydrateImages() {
    yfHydrateDrawerImages(this);
  }

  watchDrawerImages() {
    // Re-renders (quantity change, remove, offers, section refresh) insert fresh deferred images.
    if ("MutationObserver" in window) {
      this.imageObserver = new MutationObserver(() => {
        if (this.isOpen) this.hydrateImages();
      });
      this.imageObserver.observe(this, { childList: true, subtree: true });
    }
    // Start the downloads a moment early when the shopper shows intent to open the cart.
    const warm = () => this.hydrateImages();
    document.querySelectorAll(".wt-cart__trigger").forEach((trigger) => {
      ["pointerenter", "focus", "touchstart"].forEach((type) =>
        trigger.addEventListener(type, warm, { once: true, passive: true }),
      );
    });
  }

  rememberOpener(e) {
    const current = e?.currentTarget;
    const active = document.activeElement;

    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement && active !== document.body
          ? active
          : null;
  }

  getOpenerFallbacks() {
    const candidates = [this.openerEl];
    const openerForm = this.openerEl?.closest?.("product-form");
    if (openerForm) candidates.push(openerForm.querySelector('[type="submit"]'));
    document
      .querySelectorAll('product-form [type="submit"], .js-add-to-cart')
      .forEach((el) => candidates.push(el));
    candidates.push(document.querySelector(".wt-cart__trigger"));
    return candidates;
  }

  restoreFocusToOpener() {
    // Wait one frame so the drawer classes and inert state settle after closing.
    requestAnimationFrame(() => {
      const target = this.getOpenerFallbacks().find((el) => yfIsFocusable(el));
      if (!target) return;
      target.focus({ preventScroll: true });
      if (
        !target.getBoundingClientRect ||
        target.getBoundingClientRect().bottom < 0 ||
        target.getBoundingClientRect().top > window.innerHeight
      ) {
        target.scrollIntoView({ block: "center" });
      }
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, textarea, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => el.tabIndex >= 0 && yfIsFocusable(el),
      );

    const elements = focusableElements();
    return {
      focusableElements,
      first: elements[0],
      last: elements[elements.length - 1],
    };
  }

  temporaryHideFocusVisible() {
    if (window.__yfInputModality === "keyboard") {
      document.body.classList.remove("no-focus-visible");
      return;
    }
    document.body.classList.add("no-focus-visible");
  }

  onToggle(restoreFocus = true) {
    if (this.hasAttribute("open")) {
      this.restoreOpenRequested = false;
      this.removeAttribute("open");
      this.isOpen = false;
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
      if (this.drawer) this.drawer.inert = true;

      if (restoreFocus) this.restoreFocusToOpener();
    } else {
      this.hydrateImages();
      this.setAttribute("open", "");
      this.isOpen = true;
      if (this.drawer) this.drawer.inert = false;
      setTabindex(this.toggleEelements(), "0");
      this.temporaryHideFocusVisible();
      this.closeButton()?.focus();
    }
  }

  toggleDrawerClasses(restoreFocus = true) {
    if (!this.isOpen && this.needsFreshCart && !this.historyRestorePromise && !this.historyRestoreFailed) {
      this.restoreAfterHistory();
    }
    if (!this.isOpen && this.historyRestorePromise) {
      // Reuse the return-from-checkout request, including repeated trigger clicks.
      this.restoreOpenRequested = true;
      // Open a reserved pending shell, never cached totals or checkout controls.
      this.onToggle(restoreFocus);
      this.drawer.classList.add(this.classDrawerActive);
      this.body.classList.add(this.activeOverlayBodyClass);
      return;
    }
    if (!this.isOpen && this.historyRestoreFailed) {
      window.location.assign(window.routes.cart_url);
      return;
    }
    this.onToggle(restoreFocus);
    this.drawer.classList.toggle(this.classDrawerActive, this.isOpen);
    this.body.classList.toggle(this.activeOverlayBodyClass, this.isOpen);

    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  close(restoreFocus = true) {
    if (this.isOpen) this.toggleDrawerClasses(restoreFocus);
  }

  init() {
    window.addEventListener("pagehide", this.onPageHide);
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        // Escape inside the order note panel closes only that panel (cart-additional-features.js).
        if (e.target instanceof Element && e.target.closest(".wt-cart-features__drawer")) return;
        if (this.isOpen) {
          e.preventDefault();
          this.toggleDrawerClasses();
        }
        return;
      }

      if (isTabPressed && this.isOpen) {
        const { first, last } = this.getFocusableElements();
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        } else if (!this.contains(document.activeElement)) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        if (!this.isOpen) this.rememberOpener(e);
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      if (
        target.closest(".wt-cart__drawer__close--button") ||
        target.classList.contains("wt-cart__drawer__close") ||
        target.closest("[data-yf-cart-close]")
      ) {
        e.preventDefault();
        this.close();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) => {
      if (["history-restore", "uncertain-add"].includes(e.detail?.source)) {
        this.restoreAfterHistory();
      } else this.refreshCartDrawer(e);
    });
  }

  renderContents(parsedState, isClosedCart = true, refreshRequestId) {
    const hadDrawerFocus = this.isOpen && this.contains(document.activeElement);
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState?.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (refreshRequestId === undefined && parsedState?.sections?.["cart-drawer"]) {
      // A successful add response is newer than an in-flight history restore.
      ++this.cartRefreshId;
      this.cartRefreshPromise = Promise.resolve(true);
      this.historyRestoreFailed = false;
      this.needsFreshCart = false;
      this.removeAttribute("data-yf-cart-pending");
    }
    if (this.isOpen) setTabindex(this.toggleEelements(), "0");
    if (hadDrawerFocus && !this.contains(document.activeElement)) this.closeButton()?.focus();
    if (this.isOpen || isClosedCart) this.hydrateImages();

    const addedMessage = this.getAddedMessage(parsedState);
    if (addedMessage) this.showAdded(addedMessage);

    if (isClosedCart) {
      setTimeout(() => {
        if (!this.isOpen) this.toggleDrawerClasses();
        if (this.isOpen) this.closeButton()?.focus();
      });
    }
  }

  getAddedMessage(parsedState) {
    const items = Array.isArray(parsedState?.items)
      ? parsedState.items
      : parsedState?.variant_id
        ? [parsedState]
        : [];
    if (!items.length) return "";
    const labels = items
      .map((item) => {
        const line = this.querySelector(
          `.cart-item[data-variant-id="${item.variant_id}"]`,
        );
        if (!line) return item.product_title || "";
        const { yfName, yfSize } = line.dataset;
        return yfSize ? `${yfName} · ${yfSize}` : yfName;
      })
      .filter(Boolean);
    if (!labels.length) return "";
    return `Added: ${labels.join(" and ")}`;
  }

  // Sprint 1C fix r2: the visible "Added: …" line is written at once, in the same task that renders the new drawer
  // (before a closed drawer opens), so it never appears 150 ms later and pushes the lines and offers down (CLS).
  // Screen readers hear it through the separate live region, which keeps the blank-and-refill (announce()).
  showAdded(message) {
    const status = this.querySelector("[data-yf-cart-added]");
    if (!status) return;
    if (status.textContent !== message) status.textContent = message;
    this.announce(message);
  }

  announce(message) {
    const region = this.querySelector("#CartDrawer-LiveRegionText");
    if (!region || !message) return;
    region.textContent = "";
    setTimeout(() => {
      region.textContent = message;
    }, 150);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const node = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return node ? node.innerHTML : "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  // Royal fix r2 (QA r2 M1): on a Royal Goose Down page (snippets/cart-drawer.liquid sets data-yf-page-sections) the
  // drawer re-renders against the page's own URL, so its product-page branches (the Royal empty cart and its trust lines,
  // snippets/cart-empty + cart-trust) hold after the drawer opens. Every other page keeps the root URL, byte for byte.
  sectionsBase(fallback) {
    return this.hasAttribute("data-yf-page-sections") ? window.location.pathname : fallback;
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    const requestId = ++this.cartRefreshId;
    const controller = new AbortController();
    // A stalled connection must not leave the cart trigger waiting indefinitely.
    const timeout = setTimeout(() => controller.abort(), 5000);
    const cartState = fetch(`${window.Shopify.routes.root}cart.js`, {
      cache: "no-store", credentials: "same-origin", signal: controller.signal,
    }).then((response) => response.ok ? response.json() : null).catch(() => null);
    const request = fetch(
      `${this.sectionsBase(window.Shopify.routes.root)}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
      { cache: "no-store", credentials: "same-origin", signal: controller.signal },
    )
      .then((response) => {
        if (!response.ok) throw new Error("Cart refresh failed");
        return response.json();
      })
      .then(async (response) => {
        const cart = await cartState;
        // A newer cart mutation may have requested fresher sections while this was in flight.
        if (requestId !== this.cartRefreshId) return this.cartRefreshPromise;
        if (!sectionsToRender.every((section) => typeof response[section.id] === "string")) {
          throw new Error("Cart sections unavailable");
        }
        this.renderContents({ sections: response }, false, requestId);
        this.historyRestoreFailed = false;
        if (cart && typeof cart.item_count === "number") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-drawer-refresh", cartData: cart });
        }
        return true;
      })
      .catch((error) => {
        if (requestId !== this.cartRefreshId) return this.cartRefreshPromise;
        console.error(error);
        return false;
      })
      .finally(() => clearTimeout(timeout));
    this.cartRefreshPromise = request;
    return request;
  }

  setActiveElement(element) {
    this.activeElement = element;
    if (element instanceof HTMLElement && element !== document.body) {
      this.openerEl = element;
    }
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    // Royal fix r2: the same page-URL rule as CartDrawerSection.sectionsBase() (Royal Goose Down pages only).
    const drawerHost = this.closest("cart-drawer");
    const base = drawerHost && drawerHost.hasAttribute("data-yf-page-sections") ? window.location.pathname : routes.cart_url;
    fetch(`${base}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const source = html.querySelector("cart-drawer-items");
        if (source) {
          this.innerHTML = source.innerHTML;
          const drawer = this.closest("cart-drawer");
          if (drawer?.isOpen) setTabindex(drawer.toggleEelements(), "0");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
